import React, { useState, useRef, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { getPublicMenu } from "../../services/MenuService";
import CurrencySelector from "../common/CurrencySelector";
import "./SiteHeader.css";

/* ── Static fallback (used until API responds) ─────────────────────── */
const FALLBACK_MENU = {
  sections: [
    {
      id: "courses", label: "Courses", type: "mega", order: 0, enabled: true,
      promoTitle: "Trending Now", promoCtaLabel: "Browse All Courses", promoCtaUrl: "/courses",
      groups: [
        { name: "Technology", order: 0, enabled: true, items: [
          { label: "Full Stack Development", courseCategory: "Full Stack Development" },
          { label: "AI & Machine Learning",  courseCategory: "AI & Machine Learning" },
          { label: "Data Science",           courseCategory: "Data Science" },
          { label: "Cyber Security",         courseCategory: "Cyber Security" },
          { label: "Cloud Computing",        courseCategory: "Cloud Computing" },
          { label: "DevOps",                 courseCategory: "DevOps" },
        ]},
        { name: "Business", order: 1, enabled: true, items: [
          { label: "Project Management", courseCategory: "Project Management" },
          { label: "Product Management", courseCategory: "Product Management" },
          { label: "Business Analytics", courseCategory: "Business Analytics" },
          { label: "Leadership",         courseCategory: "Leadership" },
        ]},
        { name: "Design", order: 2, enabled: true, items: [
          { label: "UI/UX Design",   courseCategory: "UI/UX Design" },
          { label: "Graphic Design", courseCategory: "Graphic Design" },
        ]},
        { name: "Marketing", order: 3, enabled: true, items: [
          { label: "Digital Marketing",     courseCategory: "Digital Marketing" },
          { label: "Performance Marketing", courseCategory: "Performance Marketing" },
          { label: "SEO",                   courseCategory: "SEO" },
        ]},
      ],
      links: [],
    },
    {
      id: "careers", label: "Career Paths", type: "mega", order: 1, enabled: true,
      groups: [
        { name: "Career Paths", order: 0, enabled: true, items: [
          { label: "Software Engineer",      url: "/career-paths/software-engineer" },
          { label: "Data Scientist",         url: "/career-paths/data-scientist" },
          { label: "AI Engineer",            url: "/career-paths/ai-engineer" },
          { label: "Cloud Architect",        url: "/career-paths/cloud-architect" },
          { label: "Cyber Security Analyst", url: "/career-paths/cyber-security-analyst" },
          { label: "Product Manager",        url: "/career-paths/product-manager" },
          { label: "Digital Marketer",       url: "/career-paths/digital-marketer" },
        ]},
        { name: "For Students", order: 1, enabled: true, items: [
          { label: "Internship Programs",    url: "/programs/internships" },
          { label: "Job Ready Bootcamps",    url: "/programs/job-ready-bootcamps" },
          { label: "Certification Programs", url: "/programs/certifications" },
          { label: "Campus Training",        url: "/programs/campus-training" },
          { label: "Placement Assistance",   url: "/programs/placement-assistance" },
        ]},
        { name: "For Professionals", order: 2, enabled: true, items: [
          { label: "Upskilling Programs",    url: "/programs/upskilling" },
          { label: "Executive Programs",     url: "/programs/executive" },
          { label: "Career Switch Programs", url: "/programs/career-switch" },
          { label: "Certification Courses",  url: "/programs/certifications" },
        ]},
      ],
      links: [],
    },
    {
      id: "business", label: "For Business", type: "mega", order: 2, enabled: true,
      promoTitle: "Scale Your Workforce",
      promoText: "Custom learning solutions for teams of 10 to 10,000+",
      promoCtaLabel: "Request a Demo", promoCtaUrl: "/enterprise/request-demo",
      groups: [
        { name: "Solutions", order: 0, enabled: true, items: [
          { label: "Corporate Training",         url: "/enterprise/corporate-training" },
          { label: "Workforce Upskilling",       url: "/enterprise/workforce-upskilling" },
          { label: "Custom Learning Paths",      url: "/enterprise/custom-learning" },
          { label: "Learning Management System", url: "/enterprise/lms" },
          { label: "Request Demo",               url: "/enterprise/request-demo" },
        ]},
      ],
      links: [],
    },
    {
      id: "resources", label: "Resources", type: "simple", order: 3, enabled: true,
      groups: [],
      links: [
        { label: "Blog",                  url: "/resources/blog" },
        { label: "Success Stories",       url: "/resources/success-stories" },
        { label: "Webinars",              url: "/resources/webinars" },
        { label: "Career Guides",         url: "/resources/career-guides" },
        { label: "Interview Preparation", url: "/resources/interview-preparation" },
      ],
    },
    {
      id: "about", label: "About", type: "simple", order: 4, enabled: true,
      groups: [],
      links: [
        { label: "About Us",             url: "/about" },
        { label: "Our Mentors",          url: "/about/mentors" },
        { label: "University Partners",  url: "/about/university-partners" },
        { label: "Careers",              url: "/about/careers" },
        { label: "Contact Us",           url: "/contact" },
      ],
    },
  ],
};

/* ── Simple dropdown (Resources, About) ───────────────────────────── */
const SimpleDropdown = ({ links = [], onClose }) => (
  <div className="sh-simple-drop">
    {links.map(item => (
      <Link key={item._id || item.url} to={item.url} className="sh-simple-drop__item" onClick={onClose}>
        {item.label}
      </Link>
    ))}
  </div>
);

/* ── Resolve URL for a menu item ──────────────────────────────────── */
const itemUrl = (item) => {
  if (item.url) return item.url;
  if (item.courseCategory) return `/courses?category=${encodeURIComponent(item.courseCategory)}`;
  if (item.slug) return `/courses/${item.slug}`;
  return "/courses";
};

/* ── Component ─────────────────────────────────────────────────────── */
const SiteHeader = () => {
  const [menu,       setMenu]       = useState(null);
  const [open,       setOpen]       = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);
  const history   = useHistory();

  // Detect logged-in user from localStorage (persists across tabs/restarts)
  const loginType = localStorage.getItem("loginType") || sessionStorage.getItem("loginType");
  const isLoggedIn = !!loginType;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  let loggedInName = "";
  let dashboardPath = "/student/dashboard";
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      loggedInName = [payload.firstName, payload.lastName].filter(Boolean).join(" ") || "My Account";
      if (loginType === "teacher") dashboardPath = "/teacher/dashboard";
      else if (loginType === "superadmin") dashboardPath = "/admin/dashboard";
    } catch {}
  }
  const initials = loggedInName.split(" ").map(w => w[0]).filter(Boolean).join("").toUpperCase().slice(0, 2) || "U";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("loginType");
    sessionStorage.clear();
    setShowUserMenu(false);
    history.push("/login");
  };

  useEffect(() => {
    getPublicMenu()
      .then(r => setMenu(r.data.data || FALLBACK_MENU))
      .catch(() => setMenu(FALLBACK_MENU));
  }, []);

  useEffect(() => {
    const handler = e => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = id => setOpen(o => o === id ? null : id);
  const close  = ()  => { setOpen(null); setMobileOpen(false); };

  const sections = menu?.sections || [];

  return (
    <header className="sh" ref={headerRef}>
      <div className="sh-wrap sh__inner">

        {/* Hamburger — mobile only, left side */}
        <button className="sh-hamburger" onClick={() => setMobileOpen(v => !v)} aria-label="Menu">
          <span /><span /><span />
        </button>

        {/* LOGO */}
        <Link to="/" className="sh-brand" onClick={close}>
          <img src="/Magis.svg" alt="Magisters Hub" className="sh-brand__logo" />
        </Link>

        {/* NAV */}
        <nav className="sh-nav">
          {sections.map(section => (
            <div key={section.id} className={section.type === "simple" ? "sh-nav__item-wrap" : ""}>
              <button
                className={`sh-nav__btn${open === section.id ? " active" : ""}`}
                onClick={() => toggle(section.id)}
              >
                {section.label}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"
                  style={{ transform: open === section.id ? "rotate(180deg)" : "", transition: "transform .2s" }}>
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              {/* Simple dropdown inline */}
              {section.type === "simple" && open === section.id && (
                <SimpleDropdown links={section.links} onClose={close} />
              )}
            </div>
          ))}
        </nav>

        {/* RIGHT ACTIONS */}
        <div className="sh-actions">
          <CurrencySelector />

          {isLoggedIn ? (
            /* ── Logged-in user chip (blue avatar + first name) ── */
            <div style={{ position: "relative" }}>
              <button
                onClick={() => { setShowUserMenu(v => !v); close(); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "none", border: "none",
                  padding: "4px 6px",
                  cursor: "pointer", fontFamily: "inherit"
                }}
              >
                {/* Blue circle with person icon — matches reference */}
                <span style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 36, height: 36, borderRadius: "50%",
                  background: "#1a73e8", color: "#fff",
                  flexShrink: 0,
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                  </svg>
                </span>
                {/* First name only */}
                <span style={{ fontSize: 14, fontWeight: 500, color: "#333", whiteSpace: "nowrap" }}>
                  {loggedInName.split(" ")[0]}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#888"
                  style={{ transform: showUserMenu ? "rotate(180deg)" : "", transition: "transform .2s", flexShrink: 0 }}>
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>

              {showUserMenu && (
                <div style={{
                  position: "absolute", right: 0, top: "calc(100% + 8px)",
                  background: "#fff", border: "1px solid #e8e3f5",
                  borderRadius: 10, boxShadow: "0 8px 24px rgba(74,29,117,.13)",
                  minWidth: 180, zIndex: 9999, overflow: "hidden"
                }} onClick={e => e.stopPropagation()}>
                  <Link
                    to={dashboardPath}
                    onClick={() => setShowUserMenu(false)}
                    style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 16px", fontSize: 13, color: "#333", textDecoration: "none", fontWeight: 500 }}
                    onMouseOver={e => e.currentTarget.style.background = "#f7f4ff"}
                    onMouseOut={e => e.currentTarget.style.background = ""}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="#4A1D75"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
                    Dashboard
                  </Link>
                  <div style={{ borderTop: "1px solid #f0ebfa" }} />
                  <button
                    onClick={handleLogout}
                    style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 16px", fontSize: 13, color: "#e53935", background: "none", border: "none", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}
                    onMouseOver={e => e.currentTarget.style.background = "#fff5f5"}
                    onMouseOut={e => e.currentTarget.style.background = ""}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="#e53935"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
                    Logout
                  </button>
                </div>
              )}

              {/* close on outside click */}
              {showUserMenu && (
                <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setShowUserMenu(false)} />
              )}
            </div>
          ) : (
            <Link to="/login" className="sh-actions__login" onClick={close}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
              </svg>
              Login
            </Link>
          )}

          <Link to="/contact" className="sh-actions__advisor" onClick={close}>Talk to Advisor</Link>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      {mobileOpen && (
        <div className="sh-mobile-drawer">
          <div className="sh-mobile-drawer__inner">
            {sections.map(section => (
              <div key={section.id} className="sh-mobile-section">
                <button
                  className="sh-mobile-section__btn"
                  onClick={() => setOpen(o => o === section.id ? null : section.id)}
                >
                  {section.label}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"
                    style={{ transform: open === section.id ? "rotate(180deg)" : "", transition: "transform .2s", flexShrink: 0 }}>
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </button>
                {open === section.id && (
                  <div className="sh-mobile-section__links">
                    {section.groups.flatMap(g => g.items).map(item => (
                      <Link key={item._id || item.label} to={itemUrl(item)} className="sh-mobile-link" onClick={close}>
                        {item.label}
                      </Link>
                    ))}
                    {section.links.map(item => (
                      <Link key={item._id || item.url} to={item.url} className="sh-mobile-link" onClick={close}>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="sh-mobile-actions">
              {isLoggedIn ? (
                <>
                  <Link to={dashboardPath} className="sh-mobile-link sh-mobile-link--bold" onClick={close}>Dashboard</Link>
                  <button className="sh-mobile-link sh-mobile-link--red" onClick={handleLogout}>Logout</button>
                </>
              ) : (
                <Link to="/login" className="sh-mobile-link sh-mobile-link--bold" onClick={close}>Login</Link>
              )}
              <Link to="/contact" className="sh-mobile-cta" onClick={close}>Talk to Advisor</Link>
            </div>
          </div>
        </div>
      )}

      {/* MEGA MENUS — only for sections with type "mega" */}
      {sections.map(section => {
        if (section.type !== "mega" || open !== section.id) return null;
        return (
          <div key={section.id} className={`sh-mega${section.groups.length <= 1 ? " sh-mega--slim" : ""}`}>
            <div className="sh-wrap sh-mega__inner">
              <div className={section.groups.length > 1 ? "sh-mega__courses" : ""} style={section.groups.length > 1 ? { gridTemplateColumns: `repeat(${section.groups.length}, 1fr)` } : {}}>
                {section.groups.map(group => (
                  <div key={group._id || group.name} className="sh-mega__col">
                    <p className="sh-mega__group">{group.name}</p>
                    {group.items.map(item => (
                      <Link
                        key={item._id || item.label}
                        to={itemUrl(item)}
                        className="sh-mega__link"
                        onClick={close}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>

              {/* Promo panel — shown for sections that have promo content */}
              {(section.promoTitle || section.promoCtaLabel) && (
                <div className={`sh-mega__promo${section.id === "business" ? " sh-mega__promo--dark" : ""}`}>
                  {section.promoTitle && (
                    <p className="sh-mega__promo-head">{section.promoTitle}</p>
                  )}
                  {section.promoText && (
                    <p style={{ fontSize: ".85rem", color: section.id === "business" ? "rgba(255,255,255,.7)" : "var(--sh-muted)", marginBottom: 16, lineHeight: 1.65 }}>
                      {section.promoText}
                    </p>
                  )}
                  {section.id === "courses" && (
                    <div className="sh-mega__promo-tags">
                      {section.groups.flatMap(g => g.items).slice(0, 5).map(item => (
                        <Link key={item._id || item.label} to={itemUrl(item)} className="sh-mega__tag" onClick={close}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                  {section.promoCtaLabel && section.promoCtaUrl && (
                    <Link to={section.promoCtaUrl} className="sh-mega__cta-btn" onClick={close}>
                      {section.promoCtaLabel}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Overlay — only for mega menus */}
      {sections.some(s => s.type === "mega" && open === s.id) && (
        <div className="sh-overlay" onClick={close} />
      )}
    </header>
  );
};

export default SiteHeader;
